import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getApiError } from '../../lib/utils';
import StatusBadge from '../../components/shared/StatusBadge';
import { useNavigate } from 'react-router-dom';
import { Receipt, ChevronLeft, ChevronRight, Calendar, TrendingUp } from 'lucide-react';
import CustomerLayout from '../../components/layouts/CustomerLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
};

const Billing = () => {
  const navigate = useNavigate();
  const [currentBill, setCurrentBill] = useState(null);
  const [monthlyBills, setMonthlyBills] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedBillItems, setSelectedBillItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/billing/current`),
        axios.get(`${API_URL}/api/billing/history`)
      ]);
      setCurrentBill(currentRes.data);
      setMonthlyBills(historyRes.data.months || []);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to load billing'));
    } finally {
      setLoading(false);
    }
  };

  const viewMonthDetail = async (year, month) => {
    const key = `${year}-${month}`;
    if (selectedMonth === key) {
      setSelectedMonth(null);
      setSelectedBillItems([]);
      return;
    }
    setDetailLoading(true);
    setSelectedMonth(key);
    try {
      const res = await axios.get(`${API_URL}/api/billing/month/${year}/${month}`);
      setSelectedBillItems(res.data.waybills || []);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to load month details'));
    } finally {
      setDetailLoading(false);
    }
  };

  const monthName = (m) => {
    const months = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[m] || '';
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
    <div className="animate-fade-in space-y-6" data-testid="billing-page">
      <div>
        <h1 className="page-title">Billing</h1>
        <p className="text-sm text-slate-500 mt-1">View your current and past monthly bills</p>
      </div>

      {/* Current Month Bill */}
      <Card className="card-elevated border-l-4 border-l-teal-500" data-testid="current-month-bill">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <CardTitle className="card-title">Current Month</CardTitle>
                <p className="text-xs text-slate-400">{currentBill?.month_label}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900" data-testid="current-month-total">
                {formatCurrency(currentBill?.total)}
              </p>
              <p className="text-xs text-slate-400">{currentBill?.waybill_count} waybill{currentBill?.waybill_count !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </CardHeader>
        {currentBill?.waybills?.length > 0 && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Status</th>
                    <th>Destination</th>
                    <th>Date</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBill.waybills.map((wb) => (
                    <tr key={wb.waybill_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/waybill/${wb.waybill_id}`, { state: { from: '/billing' } })}>
                      <td className="font-medium text-teal-700">{wb.reference_number}</td>
                      <td><StatusBadge status={wb.status} /></td>
                      <td>{wb.destination_city}</td>
                      <td className="text-slate-500">{wb.date}</td>
                      <td className="text-right font-semibold">{formatCurrency(wb.quoted_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
        {(!currentBill?.waybills || currentBill.waybills.length === 0) && (
          <CardContent>
            <p className="text-sm text-slate-400 text-center py-4">No billable waybills this month yet</p>
          </CardContent>
        )}
      </Card>

      {/* Previous Months */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Previous Months</h2>
        {monthlyBills.length === 0 ? (
          <Card className="card-elevated">
            <CardContent className="py-8">
              <p className="text-sm text-slate-400 text-center">No previous billing history</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {monthlyBills.map((bill) => {
              const key = `${bill.year}-${bill.month}`;
              const isOpen = selectedMonth === key;
              return (
                <Card key={key} className={`card-elevated transition-all ${isOpen ? 'ring-2 ring-teal-200' : ''}`}>
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => viewMonthDetail(bill.year, bill.month)}
                    data-testid={`month-bill-${key}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Calendar className="h-4 w-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{monthName(bill.month)} {bill.year}</p>
                        <p className="text-xs text-slate-400">{bill.waybill_count} waybill{bill.waybill_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <p className="text-lg font-bold text-slate-900">{formatCurrency(bill.total)}</p>
                      {isOpen ? <ChevronLeft className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="border-t border-slate-100">
                      {detailLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600"></div>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Reference</th>
                                <th>Status</th>
                                <th>Destination</th>
                                <th>Date</th>
                                <th className="text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBillItems.map((wb) => (
                                <tr key={wb.waybill_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/waybill/${wb.waybill_id}`, { state: { from: '/billing' } })}>
                                  <td className="font-medium text-teal-700">{wb.reference_number}</td>
                                  <td><StatusBadge status={wb.status} /></td>
                                  <td>{wb.destination_city}</td>
                                  <td className="text-slate-500">{wb.date}</td>
                                  <td className="text-right font-semibold">{formatCurrency(wb.quoted_amount)}</td>
                                </tr>
                              ))}
                              {selectedBillItems.length === 0 && (
                                <tr><td colSpan="5" className="text-center text-slate-400 py-4">No waybills found</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </CustomerLayout>
  );
};

export default Billing;
